import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  Camera,
  Upload,
  Trash2,
  Save,
  Edit,
  Building2,
  CreditCard,
  Loader2,
  ShieldCheck,
  Lock,
  Eye,
  EyeOff,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import ImageCropperDialog from "@/components/ImageCropperDialog";
import {
  getEmployeeDetails,
  updateEmployeeProfilePhoto,
  updateEmployeeProfile,
  changeUserPassword,
  deleteProfilePhoto,
} from "@/services/employeeService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import CareerHistory from "../CareerHistory";
import ConfirmDialog from "@/components/ConfirmDialog";
import { formatEnumLabel } from "@/lib/utils";
import { isAuthExpiryError, getErrorMessage } from "@/utils/errorHandler";
import { useSubscription } from "@/contexts/SubscriptionContext";
import {
  IMMEDIATE_FIELDS,
  collectApprovalChanges,
  valuesMatch,
  labelFor,
} from "@/constants/employeeFieldPolicy";
import {
  submitChangeRequest,
  fetchMyChangeRequests,
  cancelChangeRequest,
} from "@/services/employeeChangeRequestService";
import {
  ChangeRequestStatusBadge,
  formatRequestDate,
} from "@/components/common/ChangeRequestStatusBadge";

const EmployeeProfile = () => {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const canUseDepartments = hasFeature("department_mapping");

  const [employeeData, setEmployeeData] = useState();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    marital_status: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    blood_group: "",
    bank_info: {
      bank_name: "",
      account_number: "",
      ifsc_code: "",
      branch: "",
    },
    two_factor_enabled: employeeData?.two_factor_enabled || false,
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
  });
  const [changeRequests, setChangeRequests] = useState([]);

  // Fields sitting in a pending request are locked for editing — a second
  // request on the same field would be rejected by the server anyway.
  const pendingFields = React.useMemo(() => {
    const fields = new Set();
    changeRequests
      .filter((request) => request.status === "pending")
      .forEach((request) =>
        (request.changes || []).forEach((change) => fields.add(change.field)),
      );
    return fields;
  }, [changeRequests]);

  const isFieldPending = (field) => pendingFields.has(field);

  // Derived from the list already fetched — no extra request for the badge.
  const pendingRequestCount = React.useMemo(
    () => changeRequests.filter((request) => request.status === "pending").length,
    [changeRequests],
  );

  // Sits under an approval-gated input: flags that a change is already waiting
  // on the admin, or — while editing — that saving will raise a request rather
  // than update the record.
  const ApprovalNote = ({ field }) => {
    if (isFieldPending(field)) {
      return (
        <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pending admin approval
        </p>
      );
    }
    if (!editing) return null;
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Requires admin approval
      </p>
    );
  };

  const loadChangeRequests = async () => {
    try {
      setChangeRequests(await fetchMyChangeRequests());
    } catch (error) {
      // A failure here must not block the profile itself from rendering.
      if (!isAuthExpiryError(error)) {
        console.error("Failed to load change requests:", error);
      }
    }
  };

  const handleCancelRequest = async (id) => {
    try {
      await cancelChangeRequest(id);
      toast.success("Request cancelled");
      loadChangeRequests();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to cancel request"));
    }
  };

  useEffect(() => {
    fetchEmployeeData();
    loadChangeRequests();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      if (user) {
        const data = await getEmployeeDetails();
        setEmployeeData(data);
      }
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error("Failed to load profile");
      }
    } finally {
      setInitialLoading(false);
    }
  };

  // Update form data when employeeData changes
  React.useEffect(() => {
    if (employeeData) {
      setFormData({
        name: employeeData?.name || "",
        email: employeeData?.email || "",
        phone: employeeData?.phone || "",
        address: employeeData?.address || "",
        marital_status: employeeData?.marital_status || "",
        emergency_contact_name: employeeData?.emergency_contact_name || "",
        emergency_contact_phone: employeeData?.emergency_contact_phone || "",
        blood_group: employeeData?.blood_group || "",
        bank_info: {
          bank_name: employeeData?.bank_info?.bank_name || "",
          account_number: employeeData?.bank_info?.account_number || "",
          ifsc_code: employeeData?.bank_info?.ifsc_code || "",
          branch: employeeData?.bank_info?.branch || "",
        },
        two_factor_enabled: employeeData?.two_factor_enabled || false,
      });
    }
  }, [employeeData]);

  const handleInputChange = (field, value) => {
    if (field.includes("bank_")) {
      const bankField = field.replace("bank_", "");
      setFormData((prev) => ({
        ...prev,
        bank_info: {
          ...prev.bank_info,
          [bankField]: value,
        },
      }));
    } else if (field === "two_factor_enabled") {
      setFormData((prev) => ({ ...prev, two_factor_enabled: value }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Read file as data URL for cropper
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedImageBase64) => {
    setUploadingPhoto(true);
    try {
      const res = await updateEmployeeProfilePhoto(croppedImageBase64);
      if (!res) return;
      fetchEmployeeData();
      toast.success("Profile photo updated successfully");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordData;

    if (!current_password || !new_password || !confirm_password) {
      toast.error("Please fill all password fields.");
      return;
    }

    if (new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }

    if (current_password === new_password) {
      toast.error("New password must be different from current password.");
      return;
    }

    if (new_password !== confirm_password) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    try {
      const res = await changeUserPassword(passwordData);
      toast.success(res.data.message);
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      console.log(err);
      toast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to change password. Please try again."
      );
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setUploadingPhoto(true);
      await deleteProfilePhoto();
      toast.success("Profile photo removed");
      fetchEmployeeData();
    } catch (error) {
      console.error("Error removing photo:", error);
      toast.error("Failed to remove photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Saving splits in two: ordinary fields are written straight away, while
  // sensitive ones (name, phone, bank details) become a single pending request
  // for the Company Admin and do not touch the record until approved.
  const handleSave = async () => {
    setSaving(true);
    try {
      const approvalChanges = collectApprovalChanges(formData, employeeData);
      const approvalCount = Object.keys(approvalChanges).length;

      // Send only the immediately-writable fields; the backend rejects the
      // gated ones outright, so including them would fail the whole save.
      const immediatePayload = {};
      IMMEDIATE_FIELDS.forEach((field) => {
        if (formData[field] !== undefined) {
          immediatePayload[field] = formData[field];
        }
      });

      const immediateChanged = IMMEDIATE_FIELDS.some(
        (field) => !valuesMatch(employeeData?.[field], formData[field]),
      );

      if (immediateChanged) {
        await updateEmployeeProfile(immediatePayload);
      }

      if (approvalCount > 0) {
        await submitChangeRequest(approvalChanges);
      }

      if (!immediateChanged && approvalCount === 0) {
        toast.info("No changes to save");
        setEditing(false);
        return;
      }

      if (approvalCount > 0) {
        const fieldNames = Object.keys(approvalChanges)
          .map((field) => labelFor(field))
          .join(", ");
        toast.success(
          immediateChanged
            ? `Profile updated. ${fieldNames} sent for admin approval.`
            : `${fieldNames} sent for admin approval.`,
        );
      } else {
        toast.success("Profile updated successfully");
      }

      fetchEmployeeData();
      loadChangeRequests();
      setEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      // A 409 means one of these fields is already awaiting a decision — show
      // the server's message, which names the offending fields.
      toast.error(getErrorMessage(error, "Failed to update profile"));
    } finally {
      setSaving(false);
    }
  };

  const getStatusClasses = (status) => {
    const map = {
      active: "bg-primary text-primary-foreground border-primary dark:bg-primary/10 dark:text-primary",
      inactive: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400",
      resigned: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400",
      terminated: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400",
      pending: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400",
      exit_scheduled: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400",
    };
    return map[status] || map.inactive;
  };



  const handleCancel = () => {
    // Reset form data to original values
    if (employeeData) {
      setFormData({
        name: employeeData?.name || "",
        email: employeeData?.email || "",
        phone: employeeData?.phone || "",
        address: employeeData?.address || "",
        marital_status: employeeData?.marital_status || "",
        emergency_contact_name: employeeData?.emergency_contact_name || "",
        emergency_contact_phone: employeeData?.emergency_contact_phone || "",
        blood_group: employeeData?.blood_group || "",
        bank_info: {
          bank_name: employeeData?.bank_info?.bank_name || "",
          account_number: employeeData?.bank_info?.account_number || "",
          ifsc_code: employeeData?.bank_info?.ifsc_code || "",
          branch: employeeData?.bank_info?.branch || "",
        },
        two_factor_enabled: employeeData?.two_factor_enabled || false,
      });
    }
    setEditing(false);
  };

  if (initialLoading) {
    return <EmployeeProfileSkeleton />;
  }

  return (
    <div>
      <div className="flex flex-col md:items-start mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          My Profile
        </h1>
        <p className="text-gray-500">
          Manage your personal information and preferences
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="flex justify-start space-x-8 overflow-x-auto overflow-y-hidden touch-pan-x bg-transparent h-auto p-0 no-scrollbar">
          <TabsTrigger value="profile" className="w-fit px-4 text-left sm:w-auto">
            Profile
          </TabsTrigger>
          <TabsTrigger value="career" className="w-fit px-4 text-left sm:w-auto">
            Career Overview
          </TabsTrigger>
          <TabsTrigger
            value="requests"
            className="w-fit px-4 text-left sm:w-auto"
          >
            Profile Update Requests
            {pendingRequestCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
              >
                {pendingRequestCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="space-y-4">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex w-full items-center gap-4">
              <div className="ml-auto flex items-center gap-2">
                {!editing ? (
                  <Button
                    onClick={() => setEditing(true)}
                    variant="outline"
                    className="w-auto shadow-sm"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    <span>Edit Profile</span>
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="min-w-[120px]"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              {/* Profile Photo Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Camera className="h-5 w-5" />
                    <span>Profile Photo</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <Avatar className="h-32 w-32 mx-auto">
                    <AvatarImage
                      src={employeeData?.profilePhotoSignedUrl}
                      alt="Profile photo"
                    />
                    <AvatarFallback className="text-2xl bg-primary text-primary-foreground dark:bg-primary/10 dark:text-primary">
                      {employeeData?.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("") || user?.username?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  <div className="space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      className="hidden"
                    />

                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="w-full"
                      size="sm"
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {employeeData?.profilePhotoSignedUrl
                            ? "Change Photo"
                            : "Upload Photo"}
                        </>
                      )}
                    </Button>

                    {employeeData?.profilePhotoSignedUrl && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setConfirmOpen(true);
                          setConfirmData({
                            title: "Remove Profile Photo",
                            message:
                              "Are you sure you want to remove your profile photo?",
                          });
                        }}
                        disabled={uploadingPhoto}
                        className="w-full"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Photo
                      </Button>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Supported: JPG, PNG, GIF. Max size: 5MB
                  </p>
                </CardContent>
              </Card>

              {/* Personal Information */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="h-5 w-5" />
                    <span>Personal Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Full Name
                      </label>
                      <Input
                        id="name"
                        value={
                          editing ? formData.name : employeeData?.name || ""
                        }
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        disabled={!editing || isFieldPending("name")}
                      />
                      <ApprovalNote field="name" />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Email Address
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={employeeData?.email || ""}
                        disabled={true}
                        className="cursor-not-allowed dark:text-gray-400"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Email can only be updated by admin
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        value={
                          editing ? formData.phone : employeeData?.phone || ""
                        }
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        disabled={!editing || isFieldPending("phone")}
                      />
                      <ApprovalNote field="phone" />
                    </div>
                    <div>
                      <label
                        htmlFor="marital_status"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Marital Status
                      </label>
                      {editing ? (
                        <Select
                          value={formData.marital_status}
                          onValueChange={(value) =>
                            handleInputChange("marital_status", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">Single</SelectItem>
                            <SelectItem value="married">Married</SelectItem>
                            <SelectItem value="divorced">Divorced</SelectItem>
                            <SelectItem value="widowed">Widowed</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={employeeData?.marital_status || ""}
                          disabled
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="gender"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Gender
                      </label>
                      <Input
                        id="gender"
                        value={
                          employeeData?.gender
                            ? employeeData.gender.charAt(0).toUpperCase() +
                              employeeData.gender.slice(1)
                            : ""
                        }
                        disabled
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="date_of_birth"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Date of Birth
                      </label>
                      <Input
                        id="date_of_birth"
                        value={
                          employeeData?.date_of_birth
                            ? new Date(
                                employeeData.date_of_birth,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : ""
                        }
                        disabled
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="address"
                      className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                    >
                      Address
                    </label>
                    <Textarea
                      id="address"
                      value={
                        editing ? formData.address : employeeData?.address || ""
                      }
                      onChange={(e) =>
                        handleInputChange("address", e.target.value)
                      }
                      disabled={!editing}
                      rows={3}
                    />
                  </div>

                  {/* Emergency Contact */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                      Emergency Contact & Medical Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="emergency_contact_name"
                          className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                        >
                          Emergency Contact Name
                        </label>
                        <Input
                          id="emergency_contact_name"
                          value={
                            editing
                              ? formData.emergency_contact_name
                              : employeeData?.emergency_contact_name || ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              "emergency_contact_name",
                              e.target.value,
                            )
                          }
                          disabled={!editing}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="emergency_contact_phone"
                          className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                        >
                          Emergency Contact Phone
                        </label>
                        <Input
                          id="emergency_contact_phone"
                          value={
                            editing
                              ? formData.emergency_contact_phone
                              : employeeData?.emergency_contact_phone || ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              "emergency_contact_phone",
                              e.target.value,
                            )
                          }
                          disabled={!editing}
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label
                          htmlFor="blood_group"
                          className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                        >
                          Blood Group
                        </label>
                        {editing ? (
                          <Select
                            value={formData.blood_group}
                            onValueChange={(value) =>
                              handleInputChange("blood_group", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select blood group" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="A+">
                                A RhD positive (A+)
                              </SelectItem>
                              <SelectItem value="A-">
                                A RhD negative (A-)
                              </SelectItem>
                              <SelectItem value="B+">
                                B RhD positive (B+)
                              </SelectItem>
                              <SelectItem value="B-">
                                B RhD negative (B-)
                              </SelectItem>
                              <SelectItem value="O+">
                                O RhD positive (O+)
                              </SelectItem>
                              <SelectItem value="O-">
                                O RhD negative (O-)
                              </SelectItem>
                              <SelectItem value="AB+">
                                AB RhD positive (AB+)
                              </SelectItem>
                              <SelectItem value="AB-">
                                AB RhD negative (AB-)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={employeeData?.blood_group || ""}
                            disabled
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Employment Details */}
            <div className="grid lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Building2 className="h-5 w-5" />
                    <span>Employment Details</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Employee ID
                      </p>
                      <p className="font-semibold">
                        {employeeData?.employee_id}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Status
                      </p>
                      <Badge
                        className={`${getStatusClasses(employeeData?.status)} border`}
                      >
                        {formatEnumLabel(employeeData?.status)}
                      </Badge>
                    </div>
                  </div>

                  <div
                    className={
                      canUseDepartments
                        ? "grid grid-cols-2 gap-4"
                        : "grid grid-cols-1 gap-4"
                    }
                  >
                    {/* Departments are a paid feature — hidden, not blanked.
                        The stored value is untouched and returns on upgrade. */}
                    {canUseDepartments && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Department
                        </p>
                        <p className="font-semibold">
                          {employeeData?.department}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Designation
                      </p>
                      <p className="font-semibold">
                        {employeeData?.designation}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Date of Joining
                      </p>
                      <p className="font-semibold">
                        {employeeData?.date_of_joining
                          ? new Date(
                              employeeData.date_of_joining,
                            ).toLocaleDateString()
                          : "Not available"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Work Location
                      </p>
                      <p className="font-semibold">
                        {employeeData?.work_location}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <CreditCard className="h-5 w-5" />
                    <span>Bank Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="bank_name"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Bank Name
                      </label>
                      <Input
                        id="bank_name"
                        value={
                          editing
                            ? formData.bank_info.bank_name
                            : employeeData?.bank_info?.bank_name || ""
                        }
                        onChange={(e) =>
                          handleInputChange("bank_bank_name", e.target.value)
                        }
                        disabled={!editing || isFieldPending("bank_info.bank_name")}
                      />
                      <ApprovalNote field="bank_info.bank_name" />
                    </div>
                    <div>
                      <label
                        htmlFor="branch"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Branch
                      </label>
                      <Input
                        id="branch"
                        value={
                          editing
                            ? formData.bank_info.branch
                            : employeeData?.bank_info?.branch || ""
                        }
                        onChange={(e) =>
                          handleInputChange("bank_branch", e.target.value)
                        }
                        disabled={!editing || isFieldPending("bank_info.branch")}
                      />
                      <ApprovalNote field="bank_info.branch" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="account_number"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Account Number
                      </label>
                      <Input
                        id="account_number"
                        value={
                          editing
                            ? formData.bank_info.account_number
                            : employeeData?.bank_info?.account_number || ""
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "bank_account_number",
                            e.target.value,
                          )
                        }
                        disabled={!editing || isFieldPending("bank_info.account_number")}
                      />
                      <ApprovalNote field="bank_info.account_number" />
                    </div>
                    <div>
                      <label
                        htmlFor="ifsc_code"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        IFSC Code
                      </label>
                      <Input
                        id="ifsc_code"
                        value={
                          editing
                            ? formData.bank_info.ifsc_code
                            : employeeData?.bank_info?.ifsc_code || ""
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "bank_ifsc_code",
                            e.target.value.toUpperCase(),
                          )
                        }
                        disabled={!editing || isFieldPending("bank_info.ifsc_code")}
                      />
                      <ApprovalNote field="bank_info.ifsc_code" />
                    </div>
                  </div>
                </CardContent>
              </Card>


              {/* Security Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <ShieldCheck className="h-5 w-5" />
                    <span>Security Settings</span>
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                        Two-Factor Authentication (2FA)
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Add an extra layer of security to your account.
                      </p>
                    </div>

                    <label className="inline-flex items-center cursor-pointer">
                      <Switch
                        checked={
                          editing
                            ? formData.two_factor_enabled
                            : employeeData?.two_factor_enabled
                        }
                        disabled={!editing}
                        onCheckedChange={(value) =>
                          handleInputChange("two_factor_enabled", value)
                        }
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Lock className="h-5 w-5" />
                    <span>Change Password</span>
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* CURRENT PASSWORD */}
                    <div className="col-span-2">
                      <label
                        htmlFor="current_password"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Current Password
                      </label>

                      <div className="relative">
                        <Input
                          id="current_password"
                          name="current_password"
                          placeholder="Current password"
                          type={showCurrent ? "text" : "password"}
                          // 🔒 BLOCK GOOGLE PASSWORD MANAGER
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          value={passwordData.current_password}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              current_password: e.target.value,
                            })
                          }
                        />
                        {passwordData.current_password.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowCurrent((prev) => !prev)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                          >
                            {showCurrent ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* NEW PASSWORD */}
                    <div className="col-span-2 sm:col-span-1">
                      <label
                        htmlFor="new_password"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        New Password
                      </label>

                      <div className="relative">
                        <Input
                          id="new_password"
                          placeholder="New password"
                          type={showNew ? "text" : "password"}
                          value={passwordData.new_password}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              new_password: e.target.value,
                            })
                          }
                          className="pr-10"
                        />
                        {passwordData.new_password.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowNew((prev) => !prev)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                          >
                            {showNew ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* CONFIRM PASSWORD */}
                    <div className="col-span-2 sm:col-span-1">
                      <label
                        htmlFor="confirm_password"
                        className="block text-sm font-semibold text-gray-900 dark:text-gray-200 mb-1.5"
                      >
                        Confirm Password
                      </label>

                      <div className="relative">
                        <Input
                          id="confirm_password"
                          placeholder="Confirm password"
                          type={showConfirm ? "text" : "password"}
                          value={passwordData.confirm_password}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              confirm_password: e.target.value,
                            })
                          }
                          className="pr-10"
                        />
                        {passwordData.confirm_password.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowConfirm((prev) => !prev)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                          >
                            {showConfirm ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button onClick={handleChangePassword}>
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Image Cropper Dialog */}
            <ImageCropperDialog
              open={cropperOpen}
              onClose={() => {
                setCropperOpen(false);
                setSelectedImage(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              imageSrc={selectedImage}
              onCropComplete={handleCropComplete}
            />
          </div>
        </TabsContent>
        <TabsContent value="career" className="space-y-4">
          <CareerHistory />
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Change Requests</span>
              </CardTitle>
            </CardHeader>

            <CardContent>
              {changeRequests.length === 0 ? (
                <div className="py-12 text-center space-y-2">
                  <Clock className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="text-sm text-muted-foreground">
                    No change requests yet.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Editing a sensitive field — name, phone or bank details —
                    creates a request for admin approval.
                  </p>
                </div>
              ) : (
                // Capped height so a long history scrolls inside the card
                // instead of stretching the page, which is the whole point of
                // moving this off the Profile tab.
                <div className="space-y-3 max-h-[60vh] overflow-y-auto no-scrollbar-mobile pr-1 sm:pr-2">
                  {changeRequests.map((request) => (
                    <div
                      key={request._id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <ChangeRequestStatusBadge status={request.status} />
                        <span className="text-xs text-muted-foreground">
                          {formatRequestDate(request.requested_at)}
                        </span>
                      </div>

                      <div className="space-y-1">
                        {(request.changes || []).map((change) => (
                          <div
                            key={change.field}
                            className="text-sm text-gray-900 dark:text-gray-200 break-words"
                          >
                            <span className="font-medium">{change.label}:</span>{" "}
                            <span className="text-muted-foreground line-through">
                              {change.old_value || "—"}
                            </span>{" "}
                            <span aria-hidden="true">→</span>{" "}
                            <span>{change.new_value || "—"}</span>
                          </div>
                        ))}
                      </div>

                      {request.status === "rejected" &&
                        request.rejection_reason && (
                          <p className="text-xs text-red-600 dark:text-red-400">
                            Reason: {request.rejection_reason}
                          </p>
                        )}

                      {request.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelRequest(request._id)}
                        >
                          Cancel Request
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        description=""
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={handleRemovePhoto}
        destructive
      />
    </div>
  );
};

export default EmployeeProfile;


function EmployeeProfileSkeleton() {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-10 w-52" />
        <Skeleton className="h-4 w-72" />
      </div>
      <Skeleton className="h-10 w-56" />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <Skeleton className="mx-auto h-32 w-32 rounded-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
